import { AdsenseProvider } from './adsense.provider';
import { AdMobProvider } from './admob.provider';
import firebase from 'firebase/app';
import { Injectable, Inject } from '@angular/core';
import {
    AdPlacement,
    AdPlacements,
    AdsProvider,
    AdType,
    InterstitialFailure,
    InterstitialPlacementContext,
    InterstitialTriggerSource,
} from './ads.provider';
import { Capacitor } from '@capacitor/core';
import { environment } from '@env/environment';
import { BehaviorSubject, Observable } from 'rxjs';
import { IStoreService } from '../store/store-service.interface';
import { ActivityModel, ActivityMode, AnalyticsEvent, LogAnalyticsEventInteractor, UserModel, UserService } from '@together/common';

@Injectable({ providedIn: 'root' })
export class AdsService {
    private static readonly MODAL_RESUME_DELAY_MS = 100;
    private static readonly FORCE_PLACEHOLDER_ADS_STORAGE_KEY = 'debug_force_placeholder_ads';
    private static readonly INTERSTITIAL_PRELOAD_MAX_RETRIES = 3;
    private static readonly INTERSTITIAL_PRELOAD_RETRY_BASE_DELAY_MS = 1000;
    private static readonly INTERSTITIAL_PRELOAD_RETRY_JITTER = 0.2;
    private provider!: AdsProvider;
    private providerInitPromise: Promise<void>;
    private interstitialPreloadPromises = new Map<string, Promise<void>>();
    private pendingInterstitialContexts = new Map<string, Record<string, any>>();
    private wasBannerHiddenByModalObserver = false;
    private pendingModalResumeTimeoutId?: number;
    private bannerPlaceholderVisibility = new Map<string, BehaviorSubject<boolean>>();
    private lastActivityInterstitialShownAtMs: number | null = null;

    constructor(
        @Inject('StoreService') private storeService: IStoreService,
        @Inject('FirebaseRemoteConfig') private readonly remoteConfig: firebase.remoteConfig.RemoteConfig,
        private logAnalyticsEvent: LogAnalyticsEventInteractor,
        private userService: UserService,
    ) {
        this.provider = Capacitor.isNativePlatform() ? new AdMobProvider() : new AdsenseProvider();
        this.provider.setBannerPlaceholderDisplayedListener?.(() => {
            this.logAnalyticsEvent.execute(AnalyticsEvent.InHouseAdBannerDisplayed);
        });
        this.provider.setBannerPlaceholderVisibilityListener?.((containerId, isVisible) => {
            this.setBannerPlaceholderVisibility(containerId, isVisible);
        });
        this.provider.setInterstitialPlaceholderDisplayedListener?.(() => {
            this.logAnalyticsEvent.execute(AnalyticsEvent.InHouseInterstitialDisplayed);
        });
        this.provider.setInterstitialPlaceholderClickListener?.(() => {
            this.logAnalyticsEvent.execute(AnalyticsEvent.InHouseInterstitialClicked);
            this.storeService.showUpgradeModal();
        });
        this.provider.setInterstitialDismissedListener?.(placementId => {
            this.preloadNextInterstitial(placementId);
        });
        this.provider.setInterstitialShownListener?.((placementId, provider) => {
            const payload = this.pendingInterstitialContexts.get(placementId);
            if (!payload) return;
            this.logAnalyticsEvent.execute(AnalyticsEvent.InterstitialAdShown, {
                ...payload,
                provider,
            });
            this.pendingInterstitialContexts.delete(placementId);
        });
        this.provider.setInterstitialShowFailedListener?.((placementId, failure) => {
            this.logInterstitialFailure(placementId, failure);
            if (Capacitor.isNativePlatform()) {
                this.preloadNextInterstitial(placementId);
            }
        });
        this.provider.setForcePlaceholderAdsEnabled?.(this.isForcePlaceholderAdsEnabled());

        this.providerInitPromise = this.provider.init().catch(() => undefined);
        if (Capacitor.isNativePlatform()) {
            this.observeBodyClassForModals();
        }
    }

    async showBannerForPlacement(placement: AdPlacement, containerId: string) {
        const id = this.resolveBannerPlacementId(placement);
        if (!id) return;

        await this.provider.showBanner(id, containerId);
    }

    getBannerPlaceholderVisibility$(containerId: string): Observable<boolean> {
        return this.getOrCreateBannerPlaceholderSubject(containerId).asObservable();
    }

    isForcePlaceholderAdsEnabled(): boolean {
        try {
            return localStorage.getItem(AdsService.FORCE_PLACEHOLDER_ADS_STORAGE_KEY) === 'true';
        } catch {
            return false;
        }
    }

    async hideBanner() {
        await this.provider.hideBanner();
    }

    async destroyBanner() {
        await this.provider.destroyBanner?.();
    }

    async resumeBanner() {
        await this.provider.resumeBanner?.();
    }

    private resolveBannerPlacementId(placement: AdPlacement): string | null {
        const placements = Capacitor.isNativePlatform()
            ? environment.adMobConfig?.bannerPlacements
            : environment.adsenseConfig?.bannerPlacements;

        return placements?.[placement] ?? null;
    }

    supportsInterstitial() {
        return this.provider.supportsInterstitial();
    }

    async preloadConfiguredInterstitials() {
        if (!Capacitor.isNativePlatform()) {
            return;
        }

        await this.providerInitPromise;
        if (!this.provider.supportsInterstitial()) {
            return;
        }

        const placementIds = Array.from(
            new Set(Object.values(environment.adMobConfig?.interstitialPlacements || {}).filter(Boolean)),
        );
        await Promise.allSettled(
            placementIds.map(placementId => this.preloadInterstitialWithRetry(placementId)),
        );
    }

    preloadInterstitial(placementId: string) {
        const existingPreload = this.interstitialPreloadPromises.get(placementId);
        if (existingPreload) {
            return existingPreload;
        }

        const preloadPromise = this.providerInitPromise
            .then(() => this.provider.preloadInterstitial?.(placementId))
            .then(() => undefined)
            .catch(error => {
                this.interstitialPreloadPromises.delete(placementId);
                throw error;
            });
        this.interstitialPreloadPromises.set(placementId, preloadPromise);

        return preloadPromise;
    }

    /**
     * Shared eligibility check + trigger for activity-related interstitials (activity enter/exit, mid-activity completion, etc).
     */
    async showActivityInterstitialIfEligible(
        activity: ActivityModel | undefined | null,
        participants: UserModel[],
        triggerSource: InterstitialTriggerSource,
        callId: string,
    ): Promise<void> {
        if (!activity || !participants?.length) {
            return;
        }

        const user = await this.userService.getUser();
        const canShowInterstitial =
            participants.every(participant => participant.canShowAds(activity, AdType.INTERSTITIAL)) &&
            user.preferredActivityMode !== ActivityMode.Client;

        if (!canShowInterstitial) {
            return;
        }

        if (this.isActivityInterstitialRateLimited()) {
            this.logAnalyticsEvent.execute(AnalyticsEvent.InterstitialSkippedRateLimited, {
                callId,
                activityId: activity.id,
                triggerSource,
                minimumIntervalBetweenInterstitialAdsSeconds: this.getMinimumIntervalBetweenInterstitialAdsSeconds(),
            });
            return;
        }

        this.lastActivityInterstitialShownAtMs = Date.now();

        await this.showInterstitialForPlacement(AdPlacements.INTERSTITIAL, {
            callId,
            activityId: activity.id,
            activityTitle: activity.title,
            activityType: activity.category,
            context: 'call',
            triggerSource,
        });
    }

    async showInterstitialForPlacement(placement: AdPlacement, context: InterstitialPlacementContext) {
        const adId = this.resolveInterstitialPlacementId(placement);
        if (!adId) return;

        const payload = {
            placement,
            placementId: adId,
            ...context,
            provider: Capacitor.isNativePlatform() ? 'admob' : 'adsense',
        };
        this.pendingInterstitialContexts.set(adId, payload);
        this.logAnalyticsEvent.execute(AnalyticsEvent.InterstitialPlacementTriggered, payload);

        await this.interstitialPreloadPromises.get(adId)?.catch(() => undefined);
        this.interstitialPreloadPromises.delete(adId);
        await this.provider.showInterstitial(adId);
    }

    private async preloadInterstitialWithRetry(placementId: string): Promise<void> {
        for (let retry = 0; ; retry++) {
            try {
                await this.preloadInterstitial(placementId);
                return;
            } catch (error) {
                if (retry >= AdsService.INTERSTITIAL_PRELOAD_MAX_RETRIES) {
                    throw error;
                }

                const delayMs =
                    AdsService.INTERSTITIAL_PRELOAD_RETRY_BASE_DELAY_MS *
                    Math.pow(2, retry) *
                    this.getInterstitialRetryJitter();
                await new Promise(resolve => window.setTimeout(resolve, delayMs));
            }
        }
    }

    private preloadNextInterstitial(placementId: string) {
        this.interstitialPreloadPromises.delete(placementId);
        this.preloadInterstitialWithRetry(placementId).catch(() => undefined);
    }

    private logInterstitialFailure(placementId: string, failure: InterstitialFailure) {
        const payload = this.pendingInterstitialContexts.get(placementId);
        if (!payload) return;
        this.logAnalyticsEvent.execute(AnalyticsEvent.InterstitialAdFailed, {
            ...payload,
            ...failure,
        });
        this.pendingInterstitialContexts.delete(placementId);
    }

    private getInterstitialRetryJitter(): number {
        const jitter = AdsService.INTERSTITIAL_PRELOAD_RETRY_JITTER;
        return 1 - jitter + Math.random() * jitter * 2;
    }

    supportsRewarded() {
        return this.provider.supportsRewarded();
    }

    preloadRewarded() {
        return this.provider.preloadRewarded?.();
    }

    showRewarded(onReward: () => void) {
        return this.provider.showRewarded?.(onReward);
    }

    private observeBodyClassForModals() {
        let previousIsModalOpen = this.hasOpenModal();

        const observer = new MutationObserver(() => {
            const isModalOpen = this.hasOpenModal();
            if (isModalOpen === previousIsModalOpen) {
                return;
            }
            previousIsModalOpen = isModalOpen;
            this.provider.setModalOpenState?.(isModalOpen);

            if (isModalOpen) {
                this.clearPendingModalResume();
                const shouldHideBanner = document.body.classList.contains('has-ad-banner');
                if (shouldHideBanner) {
                    this.wasBannerHiddenByModalObserver = true;
                    this.provider.hideBanner();
                }
            } else {
                const shouldResumeBanner =
                    this.wasBannerHiddenByModalObserver || this.provider.shouldResumeBannerAfterModal?.();
                if (shouldResumeBanner) {
                    this.pendingModalResumeTimeoutId = window.setTimeout(() => {
                        if (this.hasOpenModal()) {
                            return;
                        }
                        this.provider.resumeBanner?.();
                        this.wasBannerHiddenByModalObserver = false;
                        this.pendingModalResumeTimeoutId = undefined;
                    }, AdsService.MODAL_RESUME_DELAY_MS);
                }
            }
        });

        observer.observe(document.body, {
            childList: true,
            subtree: false,
            attributes: true,
            attributeFilter: ['class'],
        });
    }

    /**
     * Guards against showing multiple activity interstitials (enter/exit/completed) in quick succession
     */
    private isActivityInterstitialRateLimited(): boolean {
        if (this.lastActivityInterstitialShownAtMs === null) {
            return false;
        }

        const minimumIntervalSeconds = this.getMinimumIntervalBetweenInterstitialAdsSeconds();
        if (minimumIntervalSeconds <= 0) {
            return false;
        }

        const elapsedSeconds = (Date.now() - this.lastActivityInterstitialShownAtMs) / 1000;
        return elapsedSeconds < minimumIntervalSeconds;
    }

    private getMinimumIntervalBetweenInterstitialAdsSeconds(): number {
        const configuredValue = this.remoteConfig.getNumber('minimumIntervalBetweenInterstitialAds');
        return Math.max(configuredValue, 0);
    }

    private resolveInterstitialPlacementId(placement: AdPlacement): string | null {
        const placements = Capacitor.isNativePlatform()
            ? environment.adMobConfig?.interstitialPlacements
            : environment.adsenseConfig?.interstitialPlacements;

        return placements?.[placement] ?? null;
    }

    private clearPendingModalResume() {
        if (this.pendingModalResumeTimeoutId !== undefined) {
            window.clearTimeout(this.pendingModalResumeTimeoutId);
            this.pendingModalResumeTimeoutId = undefined;
        }
    }

    private setBannerPlaceholderVisibility(containerId: string, isVisible: boolean) {
        this.getOrCreateBannerPlaceholderSubject(containerId).next(isVisible);
    }

    private getOrCreateBannerPlaceholderSubject(containerId: string): BehaviorSubject<boolean> {
        let subject = this.bannerPlaceholderVisibility.get(containerId);
        if (!subject) {
            subject = new BehaviorSubject<boolean>(false);
            this.bannerPlaceholderVisibility.set(containerId, subject);
        }

        return subject;
    }

    private hasOpenModal() {
        return document.querySelectorAll('simple-modal-wrapper').length > 0;
    }
}
